import { Button, Flex, Stack } from '@chakra-ui/react';
import { useUserContext } from 'context/UserContextProvider';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { IdentificationResultIcon } from '../../../components/Icons';
import Link from '../../../components/Link';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { DataTable } from '../../../components/Table/DataTable';
import useWorkshopsQuery from '../../../hooks/useWorkshopsQuery';
import { ContentCard } from '../../../layout/ContentCard';

const WhiteLabelOrganizationWorkshopsCard = (props: { whiteLabelOrganizationId: number }) => {
  const { whiteLabelOrganizationId } = props;
  const { t } = useTranslation();
  const { statusCodes } = useUserContext();
  const navigate = useNavigate();
  const workshopCodes = statusCodes?.workshop || {};

  const initialFilters = {
    searchString: '',
    page: 1,
    itemsPerPage: 20,
    status: [],
    organization_id: whiteLabelOrganizationId,
    country: undefined,
  };

  const localStorageData = window.localStorage.getItem('whitelabel_organization_workshops_filters');
  // @ts-ignore
  const destringifiedData = JSON.parse(localStorageData) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('whitelabel_organization_workshops_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const workshopsQuery = useWorkshopsQuery({
    searchString,
    page,
    itemsPerPage,
    whitelabelOrganizationId: whiteLabelOrganizationId,
  });
  const currentPage = (workshopsQuery?.data?.meta?.current_page as unknown as number) - 1;
  const initialLoad = searchString === '' && itemsPerPage === 20 && page === 1;

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage]);

  useEffect(() => {
    window.localStorage.setItem(
      'whitelabel_organization_workshops_filters',
      JSON.stringify({
        searchString: searchString,
        status: status,
        page: page,
      }),
    );
  }, [searchString, itemsPerPage, page]);

  const workshops = workshopsQuery.data?.data || [];

  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return <Link to={`/app/op/workshops/${row.original.id}`}>{row.original.user_readable_id}</Link>;
        },
      },
      {
        Header: t('workshop:name.label'),
        accessor: 'name',
      },
      {
        Header: t('workshop:email.label'),
        accessor: 'email',
      },
      {
        id: 'button',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Flex alignContent={'flex-end'}>
              <Button
                ml={'auto'}
                onClick={() => {
                  navigate(`/app/op/workshops/${row.original.id}`);
                }}
              >
                {t('common:details')}
              </Button>
            </Flex>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workshopCodes, searchString, itemsPerPage],
  );
  if (workshopsQuery.isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ContentCard
      isLoading={workshopsQuery.isFetching}
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('pages:chains.details.workshops.title')}
      data-test-id="chainWorkshops-overview-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setItemsPerPage(initialFilters?.itemsPerPage);
              window.localStorage.setItem('whitelabel_organization_workshops_filters', JSON.stringify(initialFilters));
            }}
            data-test-id="whitelabel-organization-workshops-table"
            data={workshops}
            globalFilter={searchString}
            setGlobalFilter={setSearchString}
            autoFocus={true}
            globalFilterPlaceholder={t('tasks:filter.search.id_name_email')}
            //@ts-ignore
            hasFilters={false}
            hasSearchInput={true}
            currentPage={currentPage}
            perPage={itemsPerPage}
            total={workshopsQuery?.data?.meta?.last_page}
            columns={columns}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            loading={workshopsQuery.isLoading}
            isMoreThenOnePage={!initialLoad || Boolean((workshopsQuery?.data?.meta?.last_page as unknown as number) > 1)}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default WhiteLabelOrganizationWorkshopsCard;
